import { format } from 'date-fns';
import { zonedTimeToUtc, format as tzFormat } from 'date-fns-tz';
import {
  CurrencyConfig,
  CurrencyPosition,
  CurrencySymbolOrCode,
  PreferenceService,
} from '../Preferences/PreferenceService';
import { ConfigService } from '../Configs/ConfigService';
import { DateFormats, DateTimeFormats, NumberFormatStyle, TimeFormats, DateOrTime, TimeZone } from './types/enum';
import { DateTimeFormatsType } from './types/types';

const DEFAULT_TIMEZONE = 'GMT';
const { locale } = PreferenceService.getPreferences();
const DEFAULT_LOCALE = locale || 'en';

const formatNumber = (number: number): string => {
  const { locale } = PreferenceService.getPreferences();
  let formattedNumber = new Intl.NumberFormat(locale || 'th', {
    style: NumberFormatStyle.DECIMAL,
  }).format(number);
  formattedNumber = formattedNumber.replace(/[\u00a0]/g, ' ');
  return formattedNumber;
};

const formatPrice = (number: number, curr: string | null = null): string => {
  const { locale, currency: currencyCode, currencyFormatConfig } = PreferenceService.getPreferences();
  const currency = curr || currencyCode;
  let formattedPrice = '';
  if (currencyFormatConfig && currency in currencyFormatConfig) {
    formattedPrice = customPriceFormat(number, currencyFormatConfig[currency]);
  } else {
    formattedPrice = new Intl.NumberFormat(locale || 'th', {
      style: currency ? NumberFormatStyle.CURRENCY : NumberFormatStyle.DECIMAL,
      ...(currency && { currency }),
    }).format(number);
    formattedPrice = formattedPrice.replace(/ /g, ' ');
    // check if price is like "$1000" then produces "$ 1000."
    if (formattedPrice.match(/^(\D)(\d)/gm)) {
      formattedPrice = formattedPrice.replace(formattedPrice[0], `${formattedPrice[0]} `);
    }
  }
  return formattedPrice;
};

const addThousandSeparators = (price: string, separator?: string): string => {
  if (Number(price) >= 1000) {
    if (price.indexOf('.') > -1) {
      return `${addThousandSeparators(price.slice(0, price.indexOf('.') - 3), separator)}${
        separator ?? ','
      }${price.slice(price.indexOf('.') - 3)}`;
    } else {
      return `${addThousandSeparators(price.slice(0, -3), separator)}${separator ?? ','}${price.slice(-3)}`;
    }
  } else {
    return price;
  }
};

const customPriceFormat = (number: number, currency: CurrencyConfig): string => {
  const configs = ConfigService.getConfigs();
  let priceString = number.toFixed(
    configs.REMOVE_PRICE_TRAILING_ZEROS && Number.isInteger(number) ? 0 : currency.decimals,
  );
  if (currency?.roundOff) {
    priceString = `${Math.round(Number(priceString))}.00`;
  }
  priceString = addThousandSeparators(priceString, currency.thousandSeparator);
  const priceArr = [...priceString];
  const decimatIndex = priceArr.lastIndexOf('.');
  decimatIndex !== -1 && priceArr.splice(decimatIndex, 1, currency.decimalSeparator);
  priceString = priceArr.join('');
  if (currency.showSymbolOrCode === CurrencySymbolOrCode.SYMBOL) {
    priceString =
      currency.symbolPosition === CurrencyPosition.BEFORE_PRICE
        ? `${currency.symbol} ${priceString}`
        : `${priceString} ${currency.symbol}`;
  } else {
    priceString =
      currency.codePosition === CurrencyPosition.BEFORE_PRICE
        ? `${currency.code} ${priceString}`
        : `${priceString} ${currency.code}`;
  }
  return priceString;
};

const formatDate = (date: string, dateFormat: DateFormats, locale?: string): string =>
  formatDateOrTime(date, { date: dateFormat }, undefined, undefined, locale, DateOrTime.DATE_ONLY);

const getDDMMYYYY = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'en-In');
  return separator ? dateString.replaceAll('/', separator) : dateString;
};
const getDDMMMYYYY = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.MEDIUM_DATE, 'en-In');
  return separator ? dateString.replaceAll('-', separator) : dateString;
};

const getMMDDYYYY = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'en-US');
  return separator ? dateString.replaceAll('/', separator) : dateString;
};

const getYYYYMMDD = (date: string, separator?: string): string => {
  const dateString = formatDate(date, DateFormats.SHORT_DATE, 'ja-JP');
  return separator ? dateString.replaceAll('/', separator) : dateString;
};
/**
 *
 * @param time contains only time value (NO DATE) Ex. 8:56:42 PM (Correct), 2024-08-22T00:46:29.957+07:00 (Incorrect)
 */
const formatTime = (
  time: string,
  timeFormat: TimeFormats,
  hour12?: boolean,
  targetTZ?: string,
  locale?: string,
): string => formatDateOrTime(`0, ${time}`, { time: timeFormat }, hour12, targetTZ, locale, DateOrTime.TIME_ONLY);

const getHHMM = (time: string, hour12?: boolean) => formatTime(time, TimeFormats.SHORT_TIME, hour12);

const getHHMMSS = (time: string, hour12?: boolean) => formatTime(time, TimeFormats.MEDIUM_TIME, hour12);

// To format date in locale and timezone specific time with internationalization
const formatDateTime = (
  dateTime?: string,
  dateTimeFormats?: DateTimeFormatsType,
  hour12?: boolean,
  targetTZ?: string,
  locale?: string,
  dateOrTime = DateOrTime.DATE_AND_TIME,
): string => formatDateOrTime(dateTime, dateTimeFormats, hour12, targetTZ, locale, dateOrTime);

const formatDateOrTime = (
  dateTime?: string,
  dateTimeFormats?: DateTimeFormatsType,
  hour12?: boolean,
  targetTZ?: string,
  locale?: string,
  dateOrTime = DateOrTime.DATE_AND_TIME,
): string => {
  const inputDateTime = dateTime ? new Date(dateTime) : new Date();

  const configs = ConfigService.getConfigs();
  const { locale: preferenceLocale } = PreferenceService.getPreferences();
  const tarTZ = targetTZ ?? (configs.NEXT_PUBLIC_TIMEZONE as string);
  const loc = locale ?? (preferenceLocale || (configs.NEXT_PUBLIC_LOCALE as string)) ?? DEFAULT_LOCALE;

  const date = dateTimeFormats?.date ?? DateFormats.SHORT_DATE;
  const time = dateTimeFormats?.time ?? TimeFormats.SHORT_TIME;

  switch (dateOrTime) {
    case DateOrTime.DATE_ONLY:
      // Using the current timezone (timezone of wherever this code runs) - Intl.DateTimeFormat().resolvedOptions().timeZone - so that the date doesn't change
      return Intl.DateTimeFormat(loc, {
        ...DateTimeFormats[date || DateFormats.SHORT_DATE],
        timeZone: tarTZ || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).format(inputDateTime);

    case DateOrTime.TIME_ONLY:
      return Intl.DateTimeFormat(loc, {
        ...DateTimeFormats[time || TimeFormats.SHORT_TIME],
        timeZone: tarTZ || DEFAULT_TIMEZONE,
        hour12: hour12 ?? true,
      }).format(inputDateTime);

    // DateOrTime.DATE_AND_TIME
    default:
      return Intl.DateTimeFormat(loc, {
        ...DateTimeFormats[date || DateFormats.SHORT_DATE],
        ...DateTimeFormats[time || TimeFormats.SHORT_TIME],
        timeZone: tarTZ || DEFAULT_TIMEZONE,
        hour12: hour12 ?? true,
      }).format(inputDateTime);
  }
};

// To format date in custom formats
/**
 * @deprecated Please use formatDateTime, formatDate, or formatTime instead.
 */
const dateTimeToCustomFormat = (inputDate: string, formatStr: string): string => format(new Date(inputDate), formatStr);

const replaceTimeZoneToSpecified = (date: Date, toTimeZone?: TimeZone, fromTimeZone?: TimeZone) => {
  const configs = ConfigService.getConfigs();
  toTimeZone = toTimeZone ? toTimeZone : (configs.NEXT_PUBLIC_TIMEZONE as TimeZone);
  fromTimeZone = fromTimeZone ? fromTimeZone : (Intl.DateTimeFormat().resolvedOptions().timeZone as TimeZone);
  const dateZoned = zonedTimeToUtc(date, fromTimeZone);
  const convertedDate = tzFormat(dateZoned, 'yyyy-MM-dd HH:mm:ss zzz', { timeZone: toTimeZone });
  return convertedDate;
};

export {
  formatNumber,
  formatPrice,
  customPriceFormat,
  formatDate,
  getDDMMYYYY,
  getDDMMMYYYY,
  getMMDDYYYY,
  getYYYYMMDD,
  formatTime,
  getHHMM,
  getHHMMSS,
  formatDateTime,
  dateTimeToCustomFormat,
  replaceTimeZoneToSpecified,
  DateFormats,
  TimeFormats,
  DateOrTime,
  TimeZone,
};
